/**
 * Restores mouse-wheel and touch scrolling inside modals, drawers and alert
 * dialogs.
 *
 * Chakra blocks background scrolling by wrapping the bottom-most open modal in
 * react-remove-scroll, which listens for wheel/touchmove on `document` and
 * cancels every scroll it cannot attribute to that modal's own React subtree.
 * A modal declared as a *sibling* of the one holding the lock — the common
 * shape here, where a screen-level modal renders its follow-up modals next to
 * itself — is invisible to that check, so its scrollbar appears but refuses to
 * move under the wheel.
 *
 * Installing before any modal mounts puts this listener ahead of the library's
 * on `document`, so scrolls landing inside a modal are handed straight to the
 * browser and the library never sees them. Scrolls outside a modal are left
 * alone and stay blocked as intended. `overscrollBehavior: contain` on the
 * modal scroll ports (see `theme.ts`) stops a modal that is scrolled to its end
 * from chaining into the page behind it.
 *
 * Only listeners bound to `document` are skipped. React delegates from the root
 * and portal containers, which sit below `document`, so component handlers and
 * element-level listeners still run normally.
 */

// Menus and popovers are included because they portal into `body` as well, so a
// long scrollable list in one is blocked for the same reason.
const MODAL_SCROLL_PORT_SELECTOR = [
  ".chakra-modal__content-container",
  ".chakra-menu__menu-list",
  ".chakra-popover__content",
].join(", ");

let uninstall: (() => void) | null = null;

export function installModalScrollFix(): () => void {
  if (typeof document === "undefined") return () => {};
  if (uninstall) return uninstall;

  const allowNativeScroll = (event: Event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest(MODAL_SCROLL_PORT_SELECTOR)) return;
    event.stopImmediatePropagation();
  };

  // Passive: this only ever stops propagation, and a permanently non-passive
  // touchmove listener on `document` would cost mobile scrolling performance.
  const options: AddEventListenerOptions = { passive: true };
  document.addEventListener("wheel", allowNativeScroll, options);
  document.addEventListener("touchmove", allowNativeScroll, options);

  uninstall = () => {
    document.removeEventListener("wheel", allowNativeScroll, options);
    document.removeEventListener("touchmove", allowNativeScroll, options);
    uninstall = null;
  };
  return uninstall;
}
